// BasicInfoForm Component
// Form for editing basic profile information

import React, { useState } from 'react';
import { FaCamera, FaMapMarkerAlt } from 'react-icons/fa';
import styles from './BasicInfoForm.module.css';
import mediaService from '../../../services/playerProfile/mediaService';
import { useNotification } from '../../../context/NotificationContext';
import LocationPicker from '../../../components/LocationPicker/LocationPicker';
const BasicInfoForm = ({ data, onChange, userId }) => {
  const { showError, showInfo } = useNotification();
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // Ensure data exists
  if (!data) {
    return <div>Loading form...</div>;
  }

  // Handle input changes
  const handleInputChange = (field, value) => {
    const updatedData = { ...data };
    
    // Handle nested fields (supports multiple levels like contactPreferences.email.value)
    if (field.includes('.')) {
      const parts = field.split('.');
      let current = updatedData;
      
      // Navigate to the parent object
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        } else {
          current[parts[i]] = { ...current[parts[i]] };
        }
        current = current[parts[i]];
      }
      
      // Set the final value
      current[parts[parts.length - 1]] = value;
    } else {
      updatedData[field] = value;
    }
    
    onChange(updatedData);
  };

  // Handle location selection from map
  const handleLocationSelect = (locationData) => {
    const updatedData = { ...data };
    updatedData.location = locationData;
    onChange(updatedData);
    setShowLocationPicker(false);
  };

  // Handle profile picture upload
  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Validate file size (5MB max for profile pictures)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Profile picture must be less than 5MB');
      }

      // Validate file type
      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
        throw new Error('Profile picture must be JPG, PNG, or WebP');
      }
      
      setUploadingProfile(true);
      
      // Upload to Cloudinary via backend
      const formData = new FormData();
      formData.append('profilePicture', file);
      
      // Get Firebase token
      const { auth } = await import('../../../firebaseConfig');
      const token = await auth.currentUser?.getIdToken();
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/api/playerProfiles/${userId}/profile-picture`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload profile picture');
      }

      const data = await response.json();
      
      // Update the form with the Cloudinary URL
      // Backend returns the cloudinary object directly
      const profilePictureUrl = data.profilePicture?.url || 
                               data.profilePicture?.transformedUrls?.medium ||
                               data.profilePicture;
      
      handleInputChange('profilePicture', profilePictureUrl);
      showInfo('Profile picture uploaded successfully!');
      
    } catch (err) {
      showError(err.message);
    } finally {
      setUploadingProfile(false);
    }
  };

  // Handle cover photo upload
  const handleCoverPhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Validate file size (10MB max for cover photos)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Cover photo must be less than 10MB');
      }

      // Validate file type
      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
        throw new Error('Cover photo must be JPG, PNG, or WebP');
      }
      
      setUploadingCover(true);
      
      // Upload to Cloudinary via backend
      const formData = new FormData();
      formData.append('coverPhoto', file);
      
      // Get Firebase token
      const { auth } = await import('../../../firebaseConfig');
      const token = await auth.currentUser?.getIdToken();
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/api/playerProfiles/${userId}/cover-photo`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to upload cover photo');
      }

      const data = await response.json();
      
      // Update the form with the Cloudinary URL
      // Backend returns cloudinary object with url
      const coverPhotoUrl = data.coverPhoto?.url ||
                           data.coverPhoto?.transformedUrls?.desktop ||
                           data.coverPhoto;
      
      handleInputChange('coverPhoto', coverPhotoUrl);
      showInfo('Cover photo uploaded successfully!');
      
    } catch (err) {
      showError(err.message);
    } finally {
      setUploadingCover(false);
    }
  };

  return (
    <div className={styles.formSection}>
      <h2 className={styles.sectionTitle}>Basic Information</h2>
      
      {/* Profile Picture */}
      <div className={styles.profilePictureSection}>
        <label className={styles.label}>Profile Picture</label>
        <div className={styles.profilePictureContainer}>
          <div 
            className={styles.profilePicturePreview}
            style={{ 
              backgroundImage: data.profilePicture 
                ? `url(${data.profilePicture})` 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}
          >
            <label className={styles.profileUploadButton}>
              <FaCamera />
              {uploadingProfile ? 'Uploading...' : 'Change Photo'}
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                disabled={uploadingProfile}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        </div>
        <p className={styles.helperText}>
          Recommended: Square image, at least 300x300px. Max file size: 5MB
        </p>
      </div>
      
      {/* Cover Photo */}
      <div className={styles.coverPhotoSection}>
        <label className={styles.label}>Cover Photo</label>
        <div 
          className={styles.coverPhotoPreview}
          style={{ 
            backgroundImage: data.coverPhoto 
              ? `url(${data.coverPhoto})` 
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          <label className={styles.uploadButton}>
            <FaCamera />
            {uploadingCover ? 'Uploading...' : 'Change Cover Photo'}
            <input
              type="file"
              accept="image/*"
              onChange={handleCoverPhotoChange}
              disabled={uploadingCover}
              style={{ display: 'none' }}
            />
          </label>
        </div>
        <p className={styles.helperText}>
          Recommended size: 1200x400px. Max file size: 10MB
        </p>
      </div>

      {/* Name Fields */}
      <div className={styles.nameGrid}>
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="firstName">
            First Name *
          </label>
          <input
            id="firstName"
            type="text"
            className={styles.input}
            value={data.firstName || ''}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            placeholder="First name"
          />
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="lastName">
            Last Name *
          </label>
          <input
            id="lastName"
            type="text"
            className={styles.input}
            value={data.lastName || ''}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            placeholder="Last name"
          />
        </div>
      </div>
      
      {/* Gender */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Gender</label>
        <div className={styles.radioGroup}>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="gender"
              value="male"
              checked={data.gender === 'male'}
              onChange={(e) => handleInputChange('gender', e.target.value)}
            />
            <span className={styles.radioCustom}></span>
            Male
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="gender"
              value="female"
              checked={data.gender === 'female'}
              onChange={(e) => handleInputChange('gender', e.target.value)}
            />
            <span className={styles.radioCustom}></span>
            Female
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="gender"
              value="other"
              checked={data.gender === 'other'}
              onChange={(e) => handleInputChange('gender', e.target.value)}
            />
            <span className={styles.radioCustom}></span>
            Other
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="gender"
              value="prefer_not_to_say"
              checked={data.gender === 'prefer_not_to_say'}
              onChange={(e) => handleInputChange('gender', e.target.value)}
            />
            <span className={styles.radioCustom}></span>
            Prefer not to say
          </label>
        </div>
      </div>

      {/* Headline */}
      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="headline">
          Headline *
        </label>
        <input
          id="headline"
          type="text"
          className={styles.input}
          value={data.headline || ''}
          onChange={(e) => handleInputChange('headline', e.target.value)}
          placeholder="e.g., Elite Soccer Player | U19 National Team"
          maxLength={120}
        />
        <p className={styles.helperText}>
          {120 - (data.headline?.length || 0)} characters remaining
        </p>
      </div>

      {/* Bio */}
      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="bio">
          Bio
        </label>
        <textarea
          id="bio"
          className={styles.textarea}
          value={data.bio || ''}
          onChange={(e) => handleInputChange('bio', e.target.value)}
          placeholder="Tell your story... What drives you? What are your goals?"
          rows={6}
          maxLength={2000}
        />
        <p className={styles.helperText}>
          {2000 - (data.bio?.length || 0)} characters remaining
        </p>
      </div>

      {/* Location */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          <FaMapMarkerAlt /> Location
        </label>
        
        {!showLocationPicker ? (
          <>
            <div className={styles.locationDisplay}>
              {data.location?.city || data.location?.state || data.location?.country ? (
                <div className={styles.currentLocation}>
                  <FaMapMarkerAlt className={styles.locationIcon} />
                  <span>
                    {[data.location.city, data.location.state, data.location.country]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </div>
              ) : (
                <div className={styles.noLocation}>
                  <FaMapMarkerAlt className={styles.locationIcon} />
                  <span>No location set</span>
                </div>
              )}
            </div>
            
            {/* Map Picker Button (if Google Maps API is available) */}
            {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
              <button
                type="button"
                className={styles.pickLocationButton}
                onClick={() => setShowLocationPicker(true)}
              >
                <FaMapMarkerAlt />
                {data.location?.city ? 'Change Location on Map' : 'Pick Location from Map'}
              </button>
            ) : (
              /* Fallback: Manual entry if no Google Maps API */
        <div className={styles.locationGrid}>
          <input
            type="text"
            className={styles.input}
            value={data.location?.city || ''}
            onChange={(e) => handleInputChange('location.city', e.target.value)}
            placeholder="City"
          />
          <input
            type="text"
            className={styles.input}
            value={data.location?.state || ''}
            onChange={(e) => handleInputChange('location.state', e.target.value)}
            placeholder="State/Province"
          />
          <input
            type="text"
            className={styles.input}
            value={data.location?.country || ''}
            onChange={(e) => handleInputChange('location.country', e.target.value)}
            placeholder="Country"
          />
        </div>
            )}
          </>
        ) : (
          <>
            <LocationPicker
              value={data.location}
              onLocationSelect={handleLocationSelect}
            />
            <button
              type="button"
              className={styles.cancelPickerButton}
              onClick={() => setShowLocationPicker(false)}
            >
              Cancel
            </button>
          </>
        )}
      </div>

      {/* Date of Birth */}
      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="dob">
          Date of Birth
        </label>
        <input
          id="dob"
          type="date"
          className={styles.input}
          value={data.dateOfBirth || ''}
          onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
          max={new Date().toISOString().split('T')[0]}
        />
        <p className={styles.helperText}>
          Your age will be calculated automatically
        </p>
      </div>

      {/* Contact Preferences */}
      <div className={styles.formGroup}>
        <h3 className={styles.subsectionTitle}>Contact Preferences</h3>
        
        {/* Email */}
        <div className={styles.contactItem}>
          <div className={styles.contactHeader}>
            <label className={styles.label}>Email</label>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={data.contactPreferences?.email?.visible || false}
                onChange={(e) => handleInputChange('contactPreferences.email.visible', e.target.checked)}
              />
              <span className={styles.toggle}></span>
              Make visible
            </label>
          </div>
          <input
            type="email"
            className={styles.input}
            value={data.contactPreferences?.email?.value || ''}
            onChange={(e) => handleInputChange('contactPreferences.email.value', e.target.value)}
            placeholder="your.email@example.com"
          />
          {data.contactPreferences?.email?.value && (
            <p className={styles.helperText}>
              This email will be shown on your public profile when visibility is enabled
            </p>
          )}
        </div>

        {/* Phone */}
        <div className={styles.contactItem}>
          <div className={styles.contactHeader}>
            <label className={styles.label}>Phone</label>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={data.contactPreferences?.phone?.visible || false}
                onChange={(e) => handleInputChange('contactPreferences.phone.visible', e.target.checked)}
              />
              <span className={styles.toggle}></span>
              Make visible
            </label>
          </div>
          <input
            type="tel"
            className={styles.input}
            value={data.contactPreferences?.phone?.value || ''}
            onChange={(e) => handleInputChange('contactPreferences.phone.value', e.target.value)}
            placeholder="+1 (555) 123-4567"
          />
        </div>

        {/* Preferred Contact Method */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Preferred Contact Method</label>
          <div className={styles.radioGroup}>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="preferredMethod"
                value="email"
                checked={data.contactPreferences?.preferredMethod === 'email'}
                onChange={(e) => handleInputChange('contactPreferences.preferredMethod', e.target.value)}
              />
              <span className={styles.radioCustom}></span>
              Email
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="preferredMethod"
                value="phone"
                checked={data.contactPreferences?.preferredMethod === 'phone'}
                onChange={(e) => handleInputChange('contactPreferences.preferredMethod', e.target.value)}
              />
              <span className={styles.radioCustom}></span>
              Phone
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="preferredMethod"
                value="inApp"
                checked={data.contactPreferences?.preferredMethod === 'inApp'}
                onChange={(e) => handleInputChange('contactPreferences.preferredMethod', e.target.value)}
              />
              <span className={styles.radioCustom}></span>
              In-App Message
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoForm;
